import { UserImageComponent } from '@/global/user-image.component';
import { FileCategory } from '@/models/binder-file';
import { SigResult } from '@/models/sig-request.model';
import { ApprovalsService } from '@/services/api/approvals.service';
import { BinderService } from '@/services/api/binder.service';
import { DocGeneratorService } from '@/services/api/doc-generator.service';
import { UsersService } from '@/services/api/users.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { Binder } from 'src/app/models/binder.model';
import { SyncStatus } from 'src/app/models/sync-status.model';
import { UserData } from 'src/app/models/user-data.model';
import { DocDataService } from 'src/app/services/app/doc-data.service';
import { UserAccessService } from 'src/app/services/app/user-access.service';
import { FluidModule } from 'primeng/fluid';
import { DocSyncStatus } from '@/models/doc-sync-status.model';
import { DocStatusService } from '@/services/app/doc-status.service';

@Component({
    selector: 'app-approval-request',
    imports: [
        ToastModule,
        TableModule,
        ButtonModule,
        UserImageComponent,
        CommonModule,
        FluidModule,
    ],
    template: `
<p-fluid>
  <div class="flex flex-col md:flex-row gap-8">
    <div class="md:w-1/2">
      <div class="flex flex-col gap-4">
        <p-table [value]="approvalUsers" [(selection)]="selectedUsers" dataKey="UID"
          [tableStyle]="{'min-width': '30rem'}">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 4rem">
                <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
              </th>
              <th>Team Members with approval access</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-user>
            <tr>
              <td>
                <p-tableCheckbox [value]="user"></p-tableCheckbox>
              </td>
            <div
        class="flex flex-nowrap justify-between items-center border border-surface-200 dark:border-surface-700 rounded p-4  transition-colors duration-150"
        
        tabindex="0"
    >
        <div class="flex items-center">
            <div class="relative md:mr-4 flex-shrink-0">
            <app-user-image [image]="user.profilePicture" [size]="'small'"></app-user-image>
                <span
                    class="w-4 h-4 rounded-full border-2 border-surface-200 dark:border-surface-700 absolute"
                    [ngClass]="{
                        'bg-green-400': user.status === 'active',
                        'bg-red-400': user.status === 'busy',
                        'bg-yellow-400': user.status === 'away'
                    }"
                    style="bottom: 2px; right: 2px"
                ></span>
            </div>
            <div class="flex-col hidden md:flex">
                <span class="text-surface-900 dark:text-surface-0 font-semibold block">{{user.DisplayName}}</span>
                <span class="block text-surface-600 dark:text-surface-200 text-ellipsis overflow-hidden whitespace-nowrap w-40 text-sm"> 
                  <!-- title -->
                </span>
            </div>
        </div>
    </div>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="7">No users found.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
    <div class="md:w-1/2">
      <div class="flex flex-col gap-4">
        <div class="font-semibold text-xl">Submit Approval</div>
        <div class="flex flex-col gap-2">
          <p class="">Once submitted, this binder will be locked until the approval process is
            complete. This can be cancelled at any time by you or an admin.</p>
        </div>
        <div class="flex flex-col gap-2">
          <p *ngIf="selectedUsers.length < 1" class="font-medium red-italic">Please select the team members you would
            like to send the request to.</p>
        </div>
      </div>
    </div>
  </div>

  <div class="flex mt-8">
    <div class="flex flex-col gap-4 w-full">
      <div class="flex flex-wrap justify-end">
              <p-button icon="pi pi-times" [rounded]="false" [label]="'Cancel'" (click)="onCancel()">
              </p-button>
              <p-button icon="pi pi-check" [rounded]="false" class="ml-2"
                [label]="'Send Request to ' + selectedUsers.length + ' team members'"
                [disabled]="selectedUsers.length === 0" (click)="confirmRequest($event)">
              </p-button>
            </div>
        </div>
      </div>
</p-fluid>
    `,
    styles: `
.red-italic {
  color: red;
  font-style: italic;
  font-weight: 800;
}
    `
})
export class ApprovalRequestComponent {

  userListService = inject(UsersService);
  currentApprovalRequestService = inject(ApprovalsService);
  approvalsService = inject(ApprovalsService);
  confirmationService = inject(ConfirmationService);
  currentBinderService = inject(BinderService);
  userAccessService = inject(UserAccessService);
  docDataService = inject(DocDataService);
  messageService = inject(MessageService);
  binderDialogService = inject(BinderDialogService);
  docGeneratorService = inject(DocGeneratorService);
  docSyncStatusService = inject(DocStatusService);

  @Output() onApprovalRequested = new EventEmitter<boolean>();

  userList!: UserData[];
  binder!: Binder;
  loading: boolean = false;
  approvalUsers!: UserData[];
  selectedUsers: UserData[] = [];
  statusSubscription!: Subscription;
  files: FileCategory[] = [];
  response: SigResult = new SigResult();

  constructor(
    
  ) {
    effect(() => {
      const binder = this.currentBinderService.currentBinder();
      const access = this.userAccessService.binderEditAccess();
      const json = this.docDataService.jsonData();
      const userList = this.userListService.userList();
      if (binder && userList) {
        this.binder = binder;
        this.userList = userList;
        console.log('filtering approval users in constructor');
        this.filterApprovalUsers(binder.SelectedFID, userList);
      }
    });
  }

get syncStatus(): DocSyncStatus {
  return this.docSyncStatusService.syncStatus();
}

  onRequest() {
    this.loading = true;
    this.approvalsService.requestApproval(this.binder.BinderID, this.selectedUsers, this.binder.ClientLastName).subscribe(
      (data) => {
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Approval Requested' });
        this.userAccessService.setBinderEditAccess(false);
        this.loading = false;
        this.binderDialogService.closeDialog(true);
        console.log('setting request in request component', data);
        this.currentBinderService.addApproval(data);
        this.currentApprovalRequestService.setRequest(data, 5);
        this.currentBinderService.setStatus(5);
        // this.onApprovalRequested.emit(true);
      },
      error => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Approval Request Failed' });
      }
    );
  }

  toggleUser(user: UserData) {
    console.log('toggling user', user);
    if(this.selectedUsers.includes(user)) {
      this.selectedUsers = this.selectedUsers.filter(u => u.UID !== user.UID);
    } else {
      this.selectedUsers.push(user);
    }
  }

  confirmRequest(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Requesting approval will lock this binder until it is approved or this request is cancelled. Are you sure that you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: "none",
      rejectIcon: "none",
      acceptLabel: 'Request',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.onRequest()

      },
      reject: () => {
        // this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
      }
    });
  }

  onCancel() {
    this.binderDialogService.closeDialog(true);
  }

  filterApprovalUsers(fid: number, data: UserData[]) {
    console.log('users to filter', data);
    this.approvalUsers = data
      .filter(user =>
        user.UserRoles?.some(role =>
          role.PermRoles?.RoleAccess?.some(access =>
            access.AccessPermissions.dataTable === 'approvalResponses' && (access.AccessPermissions.FID === fid || access.AccessPermissions.FID === 0) && access.AccessPermissions.permType === 'edit'
          )
        )
      )
    this.selectedUsers = this.approvalUsers;
  }
}
